/**
 * kDocChartSvgText - kDocChartSvg's SVG text, summary, colour, font and label-text members.
 * Split out of kDocChartSvgCore.ts (2026-10-19, owner: smaller files for the reuse reviewer). The bodies are
 * moved VERBATIM except that `this` is the explicit first parameter `Self` (private/protected members are
 * read as Self["Name"]); kDocChartSvg keeps every member as a same-signature delegating member.
 */

import { getCoreUtils } from "./kCoreHandler";
import type { kDocTheme } from "./kDocThemeCore";
import { kDocNumberFormatCore } from "./kDocNumberFormatCore";
import type { kDocChartSvg } from "./kDocChartSvgCore";
import { kDocChartSvgMath } from "./kDocChartSvgMath";

// 2026-10-22 (owner: no runtime import cycles): this module no longer imports kDocChartSvgCore at runtime - the pure
// statics it called there live in the leaf kDocChartSvgMath; FamilyList and ThemeColors are same-signature delegates.

export class kDocChartSvgText {
    /** The SVG text. */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.Svg - its gate, try/catch and handler label "kDocChartSvg.Svg" stay verbatim, so error reports are unchanged.
    public static Svg = (Self: kDocChartSvg): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            if (Self["_Chart"].Series.length === 0 || Self["_Chart"].Categories.length === 0) { return ""; }
            Self["_Parts"] = [];
            const Kind: string = Self["_Chart"].Kind;
            if (Kind === "pie" || Kind === "donut") { Self.DrawPie(); } else { Self.DrawCartesian(); }
            return "<svg xmlns=\"http://www.w3.org/2000/svg\" width=\"" + Self.N(Self["_W"]) + "\" height=\"" + Self.N(Self["_H"])
                + "\" viewBox=\"0 0 " + Self.N(Self["_W"]) + " " + Self.N(Self["_H"]) + "\"" + Self.SummaryAttributes() + ">"
                + Self.SummaryTitle() + Self["_Parts"].join("") + "</svg>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.Svg");
            return "";
        }
    };

    // ---- the web options (kDocChartWeb; each "" / the document's output when off) -------------------------------

    /** ` role="img" aria-label="..."` when the web options carry a Summary, else "". */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.SummaryAttributes - its gate, try/catch and handler label "kDocChartSvg.SummaryAttributes" stay verbatim, so error reports are unchanged.
    public static SummaryAttributes = (Self: kDocChartSvg): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            if (Self["_Web"].Summary === "") { return ""; }
            const Aria: string = Array.from(Self["_Web"].Summary).slice(0, Math.max(1, Self["_Web"].AriaMax)).join("");
            return " role=\"img\" aria-label=\"" + kDocChartSvgMath.Escape(Aria) + "\"";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.SummaryAttributes");
            return "";
        }
    };

    /** The FIRST child `<title>` (the summary) when the web options carry one, else "". */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.SummaryTitle - its gate, try/catch and handler label "kDocChartSvg.SummaryTitle" stay verbatim, so error reports are unchanged.
    public static SummaryTitle = (Self: kDocChartSvg): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            if (Self["_Web"].Summary === "") { return ""; }
            return "<title>" + Self.EscapeText(Self["_Web"].Summary) + "</title>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.SummaryTitle");
            return "";
        }
    };

    /** A coordinate: a whole number with WholeNumbers, else `Num` (at most 2 decimals). */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.N - its gate, try/catch and handler label "kDocChartSvg.N" stay verbatim, so error reports are unchanged.
    public static N = (Self: kDocChartSvg, Value: number): string => {
        if (getCoreUtils().KErrorMode) { return "0"; }
        try {
            if (Self["_Web"].WholeNumbers === true) { return String(Math.round(Number(Value) || 0)); }
            return kDocChartSvgMath.Num(Value);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.N");
            return "0";
        }
    };

    /** Text content: as a text node with TextEscape (& < >), else `Escape` (the document's, quotes too). */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.EscapeText - its gate, try/catch and handler label "kDocChartSvg.EscapeText" stay verbatim, so error reports are unchanged.
    public static EscapeText = (Self: kDocChartSvg, Text: string): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            if (Self["_Web"].TextEscape === true) { return String(Text).split("&").join("&amp;").split("<").join("&lt;").split(">").join("&gt;"); }
            return kDocChartSvgMath.Escape(Text);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.EscapeText");
            return "";
        }
    };

    /** A series' paint: ` class="c1"` with ClassColors, else ` fill="#hex"` (the style's colour). */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.Paint - its gate, try/catch and handler label "kDocChartSvg.Paint" stay verbatim, so error reports are unchanged.
    public static Paint = (Self: kDocChartSvg, Index: number): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            if (Self["_Web"].ClassColors === true) {
                return " class=\"" + Self["_Web"].ClassPrefix + String((Index % Math.max(1, Self["_Web"].ClassCount)) + 1) + "\"";
            }
            return " fill=\"" + Self.ColorOf(Index) + "\"";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.Paint");
            return "";
        }
    };

    /** A mark's closing: `<title>...</title></Tag>` with MarkTitles, else "/>". */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.MarkEnd - its gate, try/catch and handler label "kDocChartSvg.MarkEnd" stay verbatim, so error reports are unchanged.
    public static MarkEnd = (Self: kDocChartSvg, Tag: string, S: number, I: number, Value: number): string => {
        if (getCoreUtils().KErrorMode) { return "/>"; }
        try {
            if (Self["_Web"].MarkTitles !== true) { return "/>"; }
            let Text: string = Self["_Chart"].Categories[I];
            if (Self["_Chart"].Series.length > 1) { Text = Text + " - " + Self["_Chart"].Series[S].Name; }
            Text = Text + ": " + Self.Label(Value) + Self["_Web"].TitleUnit;
            return "><title>" + Self.EscapeText(Text) + "</title></" + Tag + ">";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.MarkEnd");
            return "/>";
        }
    };

    // ---- style values --------------------------------------------------------------------------------------

    /** The text size: the style's, else the theme's table text size (R3-C-13: a chart labels its axes like a table), else 7 pt. */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.Size - its gate, try/catch and handler label "kDocChartSvg.Size" stay verbatim, so error reports are unchanged.
    public static Size = (Self: kDocChartSvg): number => {
        if (getCoreUtils().KErrorMode) { return 7; }
        try {
            let Fallback: number = 7;
            const Table: any = (Self["_Theme"] as unknown as { TableText?: unknown }).TableText;
            if (Table !== null && Table !== undefined && Number(Table.FontSizePt) > 0) { Fallback = Number(Table.FontSizePt); }
            return Self.Or(Self["_Style"].SizePt, Fallback);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.Size");
            return 7;
        }
    };

    /** The value labels' size (default: the text size). */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.LabelSize - its gate, try/catch and handler label "kDocChartSvg.LabelSize" stay verbatim, so error reports are unchanged.
    public static LabelSize = (Self: kDocChartSvg): number => {
        if (getCoreUtils().KErrorMode) { return 7; }
        try {
            return Self.Or(Self["_Style"].ValueLabelSizePt, Self.Size());
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.LabelSize");
            return 7;
        }
    };

    /** A positive number, else Fallback. */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.Or - its gate, try/catch and handler label "kDocChartSvg.Or" stay verbatim, so error reports are unchanged.
    public static Or = (Self: kDocChartSvg, Value: number, Fallback: number): number => {
        if (getCoreUtils().KErrorMode) { return Fallback; }
        try {
            if (Number(Value) > 0) { return Number(Value); }
            return Fallback;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.Or");
            return Fallback;
        }
    };

    /** The text font: the style's, the theme's diagram font, the body font. */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.Font - its gate, try/catch and handler label "kDocChartSvg.Font" stay verbatim, so error reports are unchanged.
    public static Font = (Self: kDocChartSvg): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            if (Self["_Style"].Font !== "") { return Self["_Style"].Font; }
            if (typeof Self["_Theme"].DiagramFontFamily === "string" && Self["_Theme"].DiagramFontFamily !== "") { return Self["_Theme"].DiagramFontFamily; }
            return Self["_Theme"].BodyFont;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.Font");
            return "";
        }
    };

    /**
     * R3-C-13: the font-family list - the family, then a generic one, so a viewer without the family (the document
     * view, which names its web fonts differently) shows a sans-serif, never its default serif. The PDF takes the first
     * registered family of the list (pdfMake's SVG font callback), so it prints as before.
     */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.FamilyList - its gate, try/catch and handler label "kDocChartSvg.FamilyList" stay verbatim, so error reports are unchanged.
    public static FamilyList = (Self: typeof kDocChartSvg, Family: string): string => { return kDocChartSvgMath.FamilyList(Family); };

    /** Series (or slice) Index's colour, cycling through the style's colours. */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.ColorOf - its gate, try/catch and handler label "kDocChartSvg.ColorOf" stay verbatim, so error reports are unchanged.
    public static ColorOf = (Self: kDocChartSvg, Index: number): string => {
        if (getCoreUtils().KErrorMode) { return "#000000"; }
        try {
            let Colors: string[] = Self["_Style"].SeriesColors;
            if (Array.isArray(Colors) === false || Colors.length === 0) { Colors = kDocChartSvgMath.ThemeColors(Self["_Theme"]); }
            return Colors[Index % Colors.length];
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.ColorOf");
            return "#000000";
        }
    };

    /**
     * R3-C-13 (2026-10-11): the series colours when the chart style has none - the theme's swatches (its brand
     * colours, in the order the theme lists them), else the renderer's defaults. A Citira chart printed dark grey bars.
     */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.ThemeColors - its gate, try/catch and handler label "kDocChartSvg.ThemeColors" stay verbatim, so error reports are unchanged.
    public static ThemeColors = (Self: typeof kDocChartSvg, Theme: kDocTheme): string[] => { return kDocChartSvgMath.ThemeColors(Theme); };

    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.TextColor - its gate, try/catch and handler label "kDocChartSvg.TextColor" stay verbatim, so error reports are unchanged.
    public static TextColor = (Self: kDocChartSvg): string => {
        if (getCoreUtils().KErrorMode) { return "#000000"; }
        try {
            if (typeof Self["_Theme"].TextColor === "string" && Self["_Theme"].TextColor !== "") { return Self["_Theme"].TextColor; }
            return "#000000";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.TextColor");
            return "#000000";
        }
    };

    // ---- numbers and text ------------------------------------------------------------------------------------

    /** A value as printed: the chart's decimals (or what the data needs), the locale's format, the unit. */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.Label - its gate, try/catch and handler label "kDocChartSvg.Label" stay verbatim, so error reports are unchanged.
    public static Label = (Self: kDocChartSvg, Value: number): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            let Places: number = Math.floor(Number(Self["_Chart"].Decimals));
            if (!(Places >= 0)) { Places = Self.DataDecimals(); }
            return kDocNumberFormatCore.Format(Value, Places, Self["_Locale"], Self["_Theme"]) + Self["_Chart"].Unit;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.Label");
            return "";
        }
    };

    /**
     * An axis number (R3-C-13, 2026-10-11): the chart's decimals when set; else as many as the TICKS need - whole
     * ticks print whole ("3 500", not "3 500,0" because one figure of the data has a decimal).
     */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.TickLabel - its gate, try/catch and handler label "kDocChartSvg.TickLabel" stay verbatim, so error reports are unchanged.
    public static TickLabel = (Self: kDocChartSvg, Tick: number, Ticks: number[]): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            let Places: number = Math.floor(Number(Self["_Chart"].Decimals));
            if (Places >= 0) { return Self.Label(Tick); }
            Places = 0;
            for (const Each of Ticks) { if (Math.abs(Each - Math.round(Each)) > 1e-9) { Places = 1; } }
            return kDocNumberFormatCore.Format(Tick, Places, Self["_Locale"], Self["_Theme"]) + Self["_Chart"].Unit;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.TickLabel");
            return "";
        }
    };

    /** 0 when every value is whole, else 1. */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.DataDecimals - its gate, try/catch and handler label "kDocChartSvg.DataDecimals" stay verbatim, so error reports are unchanged.
    public static DataDecimals = (Self: kDocChartSvg): number => {
        if (getCoreUtils().KErrorMode) { return 0; }
        try {
            for (const Series of Self["_Chart"].Series) {
                for (const Value of Series.Values) { if (Value !== null && Math.abs(Value - Math.round(Value)) > 1e-9) { return 1; } }
            }
            return 0;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.DataDecimals");
            return 0;
        }
    };

    /**
     * An SVG text element (anchor "start" | "middle" | "end"). ⚠ kDocChartWeb: InheritFont drops font-family,
     * ClassColors drops the fill (the page's ink), TextEscape writes the content as a text node.
     */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.Text - its gate, try/catch and handler label "kDocChartSvg.Text" stay verbatim, so error reports are unchanged.
    public static Text = (Self: kDocChartSvg, X: number, Y: number, Text: string, Anchor: string, Size: number, Color: string): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            let Family: string = " font-family=\"" + kDocChartSvgMath.Escape(kDocChartSvgMath.FamilyList(Self.Font())) + "\"";
            if (Self["_Web"].InheritFont === true) { Family = ""; }
            let Fill: string = " fill=\"" + Color + "\"";
            if (Self["_Web"].ClassColors === true) { Fill = ""; }
            let At: number = X;
            if (Self["_Web"].KeepInside === true && Anchor === "middle") {
                const Half: number = String(Text).length * Size * 0.275;
                At = Math.min(Self["_W"] - Half, Math.max(Half, X));
            }
            return "<text x=\"" + Self.N(At) + "\" y=\"" + Self.N(Y) + "\"" + Family + " font-size=\"" + kDocChartSvgMath.Num(Size)
                + "\" text-anchor=\"" + Anchor + "\"" + Fill + ">" + Self.EscapeText(Text) + "</text>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.Text");
            return "";
        }
    };

    /**
     * A centred value label over a column Column wide. Without kDocChartWeb.LabelsInColumn: exactly Text(...). With it:
     * shrunk to fit the column (estimated as Text's KeepInside does, 0.55 x size per character), down to LabelMinPct
     * of the label size; "" (left out) when even that would reach past the column into a neighbour's bar.
     */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.ColumnText - its gate, try/catch and handler label "kDocChartSvg.ColumnText" stay verbatim, so error reports are unchanged.
    public static ColumnText = (Self: kDocChartSvg, X: number, Y: number, Label: string, Column: number): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            let Size: number = Self.LabelSize();
            if (Self["_Web"].LabelsInColumn !== true) { return Self.Text(X, Y, Label, "middle", Size, Self.TextColor()); }
            const Wide: number = String(Label).length * Size * 0.55;
            if (Wide > Column) {
                Size = Size * Column / Wide;
                if (Size < Self.LabelSize() * Self["_Web"].LabelMinPct / 100) { return ""; }
            }
            return Self.Text(X, Y, Label, "middle", Size, Self.TextColor());
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.ColumnText");
            return "";
        }
    };
}
